import type { CSSProperties } from "react";
import Link from "next/link";
import Image from "next/image";
import { Stack, Typography, Box, Chip } from "@mui/material";

import type { ProjectData } from "@/types/project";

function siliconPlanHoverSx() {
  return {
    "&:hover .sil-plan-bar-top-c": { width: "120px" },
    "&:hover .sil-plan-bar-top-f": { width: "70%" },
    "&:hover .sil-plan-bar-bot-c": { width: "70px" },
    "&:hover .sil-plan-bar-bot-f": { width: "70%" },
  } as const;
}

const ProjectCard = ({ project }: { project: ProjectData }) => {
  const hrefNorm = project.link.trim();

  const isDeadLink =
    Boolean(project.isComingSoon) ||
    hrefNorm.length === 0;


  const headerAlign = project.headerAlign ?? "left";

  const shellStyle: CSSProperties = {
    textDecoration: "none",
    color: "inherit",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: "100%",
    height: "100%",
  };

  const titleSx = project.titleGradient
    ? {
        background: project.titleGradient,
        backgroundClip: "text",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
      }
    : {
        color: project.titleColor ?? "#FFF",
        ...(project.titleShadow != null
          ? { textShadow: project.titleShadow }
          : { textShadow: "0 2px 10px rgba(0,0,0,0.3)" }),
      };

  const cardBody = (
    <Stack
      className="project-card-root"
      sx={{
        width: "100%",
        minWidth: 0,
        height: "100%",
        minHeight: 0,
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
        borderLeft: { xs: "none" },
        border: { xs: "none" },
        outline: { xs: "none" },
        boxShadow: { xs: "none" },
        padding: { xs: 2, sm: 3 },
        background: project.background,
        transition: project.cardHoverTransform ? "transform 0.35s ease" : "transform 0.3s ease",
        ...(project.cardHoverTransform && {
          "&:hover": { transform: project.cardHoverTransform },
        }),
        ...(project.id === "siliconplan" ? siliconPlanHoverSx() : {}),
        ...project.rootSx,
      }}
    >
      {project.bgImage ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          <Image
            src={project.bgImage}
            alt=""
            fill
            className={project.bgImageClassName}
            style={{
              objectFit: "cover",
              objectPosition: project.bgImageObjectPosition ?? "center",
              opacity: project.bgImageOpacity ?? 1,
            }}
          />
        </Box>
      ) : null}

      {project.overlayGradient ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            background: project.overlayGradient,
            pointerEvents: "none",
          }}
        />
      ) : null}

      {project.customOverlay ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "flex-start",
            p: { xs: 2, sm: 3 },
            pointerEvents: "none",
            "& > *": { pointerEvents: "auto" },
          }}
        >
          {project.customOverlay}
        </Box>
      ) : null}

      {project.assets.map((asset, index) => (
        <Box
          key={index}
          className={`card-asset ${asset.decorClass ?? ""}`}
          sx={{
            position: "absolute",
            zIndex: asset.zIndex,
            transition:
              asset.transition ??
              "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            width: asset.width,
            transform: asset.idleTransform,
            ...asset.position,
            "& img": { width: "100%", height: "auto", display: "block" },
            ".project-card-root:hover &": {
              transform: asset.hoverTransform,
              filter: asset.hoverFilter ?? "brightness(1.1)",
            },
          }}
        >
          <Image src={asset.src} alt={asset.alt} width={400} height={400} priority={false} />
        </Box>
      ))}

      <Box
        sx={{
          display: { xs: "block", md: "none" },
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: { xs: "100%", sm: "80%" },
          background:
            "linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0) 100%)",
          zIndex: 5,
        }}
      />

      <Stack
        position="relative"
        sx={{
          zIndex: 12,
          ...(headerAlign === "center"
            ? {
                left: 0,
                mx: "auto",
                alignItems: "center",
                textAlign: "center",
                maxWidth: "100%",
              }
            : {
                left: { xs: 0, md: -8 },
                alignItems: "flex-start",
                textAlign: "left",
                maxWidth: { xs: "85%", sm: "70%", md: "100%" },
              }),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" justifyContent={headerAlign === "center" ? "center" : "flex-start"}>
          <Typography
            variant="h4"
            fontWeight={600}
            textTransform="uppercase"
            sx={{
              ...titleSx,
              fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2.125rem" },
            }}
          >
            {project.title}
          </Typography>
          {project.isComingSoon ? (
            <Chip
              label="Coming Soon"
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "#FFF",
                fontWeight: 700,
                backdropFilter: "blur(4px)",
                mt: { xs: 0.5, sm: 0 },
              }}
            />
          ) : null}
        </Stack>
        <Typography
          variant="subtitle1"
          fontWeight={500}
          sx={{
            width: "100%",
            mt: 0.5,
            whiteSpace: "pre-line",
            ...(project.subtitleOpacity != null && {
              opacity: project.subtitleOpacity,
            }),
            maxWidth:
              headerAlign === "center" ? "640px" : { xs: "95%", md: "90%" },
          }}
          color={project.subtitleColor ?? "rgba(255,255,255,0.8)"}
          lineHeight={1.3}
        >
          {project.subtitle}
        </Typography>
      </Stack>
    </Stack>
  );

  if (isDeadLink) {
    return (
      <Box component="div" style={shellStyle}>
        {cardBody}
      </Box>
    );
  }

  return (
    <Link href={hrefNorm} target="_blank" rel="noopener noreferrer" style={shellStyle}>
      {cardBody}
    </Link>
  );
};

export default ProjectCard;
